import React, { Fragment, Component } from "react";
import styled from "styled-components";
import Helmet from "react-helmet";
import PropTypes from "prop-types";
import Img from "gatsby-image";
import Flex, { FlexItem } from "styled-flex-component";
import { Combobox } from "evergreen-ui";
import { Title, SubTitle, CenteredFull } from "../components/Typography";

function encode(data) {
	return Object.keys(data)
		.map(key => encodeURIComponent(key) + "=" + encodeURIComponent(data[key]))
		.join("&");
}

export default class ConsultationPage extends Component {
	constructor() {
		super();
		this.handleChange.bind(this);
		this.handleSubmit.bind(this);
	}
	state = {
		sent: false,
		time: "Anytime"
	};

	static propTypes = {
		data: PropTypes.shape({
			markdownRemark: PropTypes.shape({
				frontmatter: PropTypes.shape({
					title: PropTypes.string
				})
			})
		})
	};

	closeForm() {
		this.setState({ sent: true });
	}

	handleChange = e => {
		this.setState({ [e.target.name]: e.target.value });
	};

	handleSubmit = e => {
		fetch("/", {
			method: "POST",
			headers: { "Content-Type": "application/x-www-form-urlencoded" },
			body: encode({ "form-name": "request-a-consultation", ...this.state })
		})
			.then(this.closeForm())
			.catch(error => alert(error));

		e.preventDefault();
	};

	render() {
		const { markdownRemark: post } = this.props.data;
		return (
			<Fragment>
				<Helmet title="Contact" />
				<ContactContainer full alignCenter>
					<FlexItem>
						<Title>{post.frontmatter.title}</Title>
						<ContactImage resolutions={post.frontmatter.image.childImageSharp.resolutions} />
						{this.state.sent ? (
							<FormConfirmation>Thank you. We'll be in touch shortly!</FormConfirmation>
						) : (
							<form
								name="request-a-consultation"
								id="request-a-consultation"
								method="POST"
								data-netlify="true"
								data-netlify-honeypot="bot-field"
								onSubmit={this.handleSubmit}>
								<input type="hidden" name="form-name" value="request-a-consultation" />
								<p hidden>
									<label>
										Don’t fill this out: <input name="bot-field" onChange={this.handleChange} />
									</label>
								</p>
								<FormGrid>
									<p>
										<TextInput
											required
											type="text"
											autoComplete="name"
											name="full name"
											placeholder="Your name"
											className="name"
											onChange={this.handleChange}
										/>
									</p>
									<p>
										<TextInput
											required
											type="email"
											autoComplete="email"
											name="email"
											placeholder="Your email"
											className="email"
											onChange={this.handleChange}
										/>
									</p>
									<p>
										<TextInput
											required
											type="phone"
											autoComplete="phone"
											name="phone"
											placeholder="Your phone"
											className="phone"
											onChange={this.handleChange}
										/>
									</p>
									<p>
										<TextInput
											required
											type="date"
											autoComplete="date"
											name="date"
											placeholder="Requested date"
											className="date"
											onChange={this.handleChange}
											style={{ webkitAppearance: "none" }}
										/>
									</p>
									<SelectContainer>
										<SelectInput
											required
											type="select"
											autoComplete="time"
											name="time"
											placeholder="Requested time"
											className="time"
											onChange={this.handleChange}>
											<option value="Anytime" selected>
												Anytime
											</option>
											<option value="morning">Morning</option>
											<option value="afternoon">Afternoon</option>
											<option value="evening">Evening</option>
										</SelectInput>
									</SelectContainer>
									<p>
										<TextArea
											required
											type="message"
											autoComplete="true"
											name="message"
											placeholder="Message"
											onChange={this.handleChange}
										/>
									</p>
								</FormGrid>
								<p>
									<Button name="submit" type="Submit" className="submit" onClick={this.handleSubmit} value="Submit">
										Submit
									</Button>
								</p>
							</form>
						)}
					</FlexItem>
				</ContactContainer>
			</Fragment>
		);
	}
}

const FormGrid = styled.div`
	display: grid;
	grid-template-columns: 1fr 1fr;
	grid-gap: 0 1rem;

	@media screen and (max-width: 1200px) {
		grid-template-columns: 1fr;
	}
`;

const FormConfirmation = styled.div`
	background: ${props => props.theme.color.darkGreen};
	color: white;
	padding: 1rem 2rem;
	border-radius: 5px;
	z-index: 999;
	position: relative;
`;

const TextInput = styled.input`
	width: 250px;
	border: none;
	padding: 10px 15px;
	outline: none;
	box-shadow: 0 3px 6px rgba(0, 0, 0, 0.12);
	border-radius: 5px;
	transition: 300ms;
	height: 25px;

	@media screen and (max-width: 1200px) {
		width: 100%;
		height: 45px;
		box-sizing: border-box;
	}
	}

	&:focus {
		box-shadow: 0 5px 10px rgba(0, 0, 0, 0.16);
	}
`;

const SelectContainer = styled.p`
	position: relative;
	&::after {
		content: "";
		position: absolute;
		z-index: 2;
		top: 24px;
		margin-top: -3px;
		height: 0;
		width: 0;
		right: 1rem;
		border-top: 6px solid black;
		border-left: 6px solid transparent;
		border-right: 6px solid transparent;
		pointer-events: none;
	}
`;

const SelectInput = TextInput.withComponent("select").extend`
background-color: white;
height: 45px;
width: 280px;
-webkit-appearance: none;
`;

const TextArea = styled.textarea`
	width: 250px;
	min-height: 100px;
	border: none;
	padding: 10px 15px;
	outline: none;
	box-shadow: 0 3px 6px rgba(0, 0, 0, 0.12);
	border-radius: 5px;
	resize: none;
	transition: 300ms;

	&:focus {
		box-shadow: 0 5px 10px rgba(0, 0, 0, 0.16);
	}

	@media screen and (max-width: 1200px) {
		width: 100%;
		box-sizing: border-box;
	}
`;

const ContactContainer = Flex.extend`
	padding: ${100 / 24}vw;
	padding-top: 0;
	background: ${props => props.theme.gradient.white};

	@media screen and (max-width: 800px) {
		flex-direction: column-reverse;
		justify-content: center;
		align-items: flex-start;
	}

	form,
	${Title} {
		position: relative;
		z-index: 99;
	}
`;

const ContactImage = styled(Img)`
	position: absolute !important;
	right: 0;
	bottom: 0;
	transform: translateY(25%) translateX(20%) scale(0.65);
	opacity: 0.65;
`;

const ContactText = FlexItem.extend`
	padding-left: ${100 / 24}vw;
	padding-right: ${(100 / 24) * 3}vw;
	max-width: 500px;

	@media screen and (max-width: 800px) {
		margin-bottom: ${(100 / 24) * 3}vw;
	}

	${SubTitle} {
		font-size: 1.75rem !important;
		line-height: 1.75;

		@media screen and (max-width: 800px) {
			font-size: 1.25rem !important;
			text-align: right;
		}
	}
`;

const FieldFlex = FlexItem.extend`
	margin: 10px;

	input,
	textarea {
		box-shadow: 0 3px 6px rgba(0, 0, 0, 0.12);
		resize: none;
		transition: ${props => props.theme.animation.normal} ease;
		border-radius: 5px;

		&:focus {
			box-shadow: 0 5px 8px rgba(0, 0, 0, 0.16);
			transform: scale(1.01);
		}
	}
`;

const Button = styled.button`
	background: ${props => props.theme.gradient.green};
	color: white;
	border-radius: 7.5px;
	box-shadow: 0 5px 10px rgba(4, 135, 56, 0.3) !important;
	transition: 225ms;
	font-family: "Bitter";
	border: none;
	padding: 0.5rem 1rem;
	margin: 0.3325rem 0;
	cursor: pointer;

	&:hover {
		box-shadow: 0 6px 12px rgba(4, 135, 56, 0.35) !important;
		transform: scale(1.025);
	}
`;

export const consultationPageQuery = graphql`
	query ConsultationPage($id: String!) {
		markdownRemark(id: { eq: $id }) {
			frontmatter {
				title
				image {
					childImageSharp {
						resolutions(width: 400) {
							...GatsbyImageSharpResolutions_withWebp
						}
					}
				}
			}
		}
	}
`;



// WEBPACK FOOTER //
// ./src/templates/consultation-page.jsx